import './MetadataForm.scss';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Alert, Form, Modal } from 'react-bootstrap';
import { AudioCompanion } from '@blisspointmedia/bpm-types/dist/Creative';
import { Button } from '@Components/Button';
import { Checkbox } from '@Components/FormCheck';
import { Appearance, Design } from '@Components/designConstants';
import { CreativeMetadataInputCard } from '@Components/CreativeAllocation';
import { TagCreationContext } from './tag-creation-context';
import { StagedTagData } from './utils';
import { useCreativeMap } from '../../../redux/creative';
import { useMap } from '../../../utils/hooks/useData';

interface AudioCompanionDropdownOption {
  label: string;
  value: string;
}

interface MetadataFormProps {
  tags: StagedTagData[];
  setTags: Dispatch<SetStateAction<StagedTagData[]>>;
  onSubmit: () => void;
  onBack?: () => void;
  company: string;
  isEditMode: boolean;
  isPlacementLevelModeInitial?: boolean;
}

export const MetadataForm = ({
  tags,
  setTags,
  onSubmit,
  onBack,
  company,
  isEditMode,
  isPlacementLevelModeInitial = false
}: MetadataFormProps) => {
  // When in placement-level mode we take a single input for a clickthrough URL
  // to set for the entire placement, rather than individual creative-level URLs.
  const initialPlacementLevelClickthroughUrl = isEditMode ? tags[0].clickthroughUrl : undefined;
  const [isPlacementLevelMode, setIsPlacementLevelMode] = useState(
    isPlacementLevelModeInitial || Boolean(initialPlacementLevelClickthroughUrl)
  );
  const [placementLevelClickthroughUrl, setPlacementLevelClickthroughUrl] = useState<
    string | undefined
  >(initialPlacementLevelClickthroughUrl);

  const [formFailMessage, setFormFailMessage] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isEditingClickthroughs, setIsEditingClickthroughs] = useState(isEditMode);

  const { creativeMap } = useCreativeMap({
    company,
    mediaTypes: ['streaming', 'audio', 'display']
  });

  const iscis: string[] = useMemo(() => {
    const uniqueIscis: string[] = [];
    for (const tag of tags) {
      for (const isci of Object.keys(tag.creativeAllocationMap || {})) {
        if (!uniqueIscis.includes(isci)) {
          uniqueIscis.push(isci);
        }
      }
    }
    return uniqueIscis;
  }, [tags]);

  const adServer = tags[0]?.adServer;
  const isAudio = tags[0]?.platform.includes('Audio');
  const { audioCompanionOptions: allCompanions } = useContext(TagCreationContext);

  const audioCompanionOptions = useMemo(
    () => (adServer === 'Flashtalking' ? allCompanions : []),
    [adServer, allCompanions]
  );

  const audioCompanionOptionsList = useMemo<AudioCompanionDropdownOption[]>(() => {
    if (!isAudio || !audioCompanionOptions.length) return [];
    return [
      { label: 'No Companion', value: '' },
      ...audioCompanionOptions.map(({ adid, title }) => ({
        label: `${adid} (${title})`,
        value: adid
      }))
    ];
  }, [isAudio, audioCompanionOptions]);

  const defaultClickthroughUrlMap = useMemo<Record<string, string>>(() => {
    if (isEditMode) {
      return tags[0].clickthroughUrlMap || {};
    }
    const defaults: Record<string, string> = {};
    for (const isci of iscis) {
      const creative = creativeMap?.[isci];
      defaults[isci] = (creative as any)?.clickthroughUrl || '';
    }
    return defaults;
  }, [isEditMode, tags, iscis, creativeMap]);

  const [clickthroughUrlMap, setClickthroughUrlValue, setClickthroughUrlMap] = useMap<
    string,
    string
  >(defaultClickthroughUrlMap);

  const [audioCompanionMap, setAudioCompanionValue, setAudioCompanionMap] = useMap<
    string,
    AudioCompanion
  >(
    {} // TODO: Get default values from creative map once audio companions are in there.
  );

  const addMetadataToTags = useCallback(() => {
    let foundInvalidClickthrough = false;
    const tagsWithMetadata = tags.map(tag => {
      const mutableTag = { ...tag };
      if (isEditMode) {
        delete mutableTag.clickthroughUrl;
        delete mutableTag.clickthroughUrlMap;
        delete mutableTag.audioCompanionMap;
      }

      if (placementLevelClickthroughUrl) {
        if (!placementLevelClickthroughUrl.includes('https')) {
          foundInvalidClickthrough = true;
          return tag;
        }
        return { ...mutableTag, clickthroughUrl: placementLevelClickthroughUrl };
      }
      for (const clickthrough of Object.values(clickthroughUrlMap)) {
        if (clickthrough && !clickthrough.includes('https')) {
          foundInvalidClickthrough = true;
          return tag;
        }
      }
      return {
        ...mutableTag,
        clickthroughUrlMap: clickthroughUrlMap as Record<string, string>,
        audioCompanionMap: audioCompanionMap as Record<string, AudioCompanion>
      };
    });

    if (foundInvalidClickthrough) {
      setFormFailMessage('Clickthrough URLs must be secure (https).');
      return;
    }

    setTags(tagsWithMetadata);
    setClickthroughUrlMap({});
    setAudioCompanionMap({});
    setIsEditingClickthroughs(false);
  }, [
    clickthroughUrlMap,
    audioCompanionMap,
    isEditMode,
    placementLevelClickthroughUrl,
    setClickthroughUrlMap,
    setAudioCompanionMap,
    setTags,
    tags
  ]);

  useEffect(() => {
    const tagsHaveMetadata = tags.reduce(
      (prev, curr) => prev && (Boolean(curr.clickthroughUrlMap) || Boolean(curr.clickthroughUrl)),
      true
    );
    if (tagsHaveMetadata && !isSubmitted && !isEditingClickthroughs) {
      onSubmit();
      setIsSubmitted(true);
    }
    if (!tagsHaveMetadata) {
      setIsSubmitted(false);
    }
  }, [onSubmit, tags, isSubmitted, isEditingClickthroughs]);

  const togglePlacementLevelCheckbox = useCallback(() => {
    if (isPlacementLevelMode) {
      setIsPlacementLevelMode(false);
      setPlacementLevelClickthroughUrl(undefined);
    } else {
      setIsPlacementLevelMode(true);
    }
  }, [isPlacementLevelMode]);

  const onClickthroughUrlInput = useCallback(
    (isci: string, value: string) => setClickthroughUrlValue(isci, value),
    [setClickthroughUrlValue]
  );

  const onAudioCompanionSelect = useCallback(
    (isci: string, value: string) => {
      const audioCompanion = audioCompanionOptions.find(o => o.adid === value);
      if (audioCompanion) {
        setAudioCompanionValue(isci, audioCompanion);
      }
    },
    [setAudioCompanionValue, audioCompanionOptions]
  );

  return (
    <div className='metadata-form-step'>
      <Modal.Header closeButton>
        <Modal.Title>Set Metadata</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!audioCompanionOptionsList.length && (
          <div className='clickthrough-type-checkbox-container'>
            <Checkbox
              checked={isPlacementLevelMode}
              onChange={togglePlacementLevelCheckbox}
              label='Set a single placement-level URL'
            />
          </div>
        )}
        <div className='metadata-form'>
          {isPlacementLevelMode ? (
            <div className='placement-level-input-container'>
              <div className='input-box-label'>Placement-Level Click-Through URL:</div>
              <Form.Control
                as='textarea'
                className='placement-level-input-box'
                value={placementLevelClickthroughUrl}
                onChange={e => setPlacementLevelClickthroughUrl(e.target.value)}
              />
            </div>
          ) : (
            <div>
              {iscis.map(isci => {
                const creative = creativeMap?.[isci];
                return creative ? (
                  <CreativeMetadataInputCard
                    company={company}
                    key={isci}
                    creative={creative}
                    clickthroughUrl={clickthroughUrlMap[isci] || ''}
                    onClickthroughUrlInput={onClickthroughUrlInput}
                    audioCompanionOptionsList={audioCompanionOptionsList}
                    audioCompanion={audioCompanionMap[isci]?.adid || ''}
                    onAudioCompanionSelect={onAudioCompanionSelect}
                  />
                ) : (
                  <div key={isci} />
                );
              })}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        {formFailMessage && (
          <Alert variant='danger' className='alert'>
            {formFailMessage}
          </Alert>
        )}
        {onBack && (
          <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onBack}>
            Back
          </Button>
        )}
        <Button onClick={addMetadataToTags}>Stage</Button>
      </Modal.Footer>
    </div>
  );
};
